import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	useLoaderData,
	useOutletContext,
	useNavigate,
	useFetcher,
	useRevalidator,
} from 'react-router';
import EmailSyncTitle from './EmailSyncTitle';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
	Box,
	Typography,
	Button,
	Paper,
	Tooltip,
	CircularProgress,
	useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GoogleIcon from '@mui/icons-material/Google';
import SyncIcon from '@mui/icons-material/Sync';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import UserEmailSyncConfigModal from './EmailSyncConfigSetting/UserEmailSyncConfigModal';
import EmailJobConfigModal from './EmailJobConfigModal';
import EmailManualSyncConfirmationModal from './EmailManualSyncConfirmationModal';
import EmailSyncHealthBar from './EmailSyncHealthBar';
import FailedIngestionCount from '../../components/FailedIngestionCount';
import { authedApiFetch } from '../../utils/Api';
import { useComposioOAuth } from '../hooks/useComposioOAuth';
import { formatDateTime } from '../../utils/utils';
import { useDataGridState } from '../hooks/useDataGridState';
import { useEmailJobsPoller } from '../hooks/useEmailJobsPoller';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
	getConnectionButtonState,
	isConnectionDisrupted,
} from 'features/EmailSync/utils/connectionUtils';

import dayjs from 'dayjs';

// TODO: move to CSS file
const styles = {
	capitalizeText: {
		'& .capitalize-text': {
			textTransform: 'capitalize',
		},
	},
};

// Loader that reads pagination/sort params from the URL and passes them to the backend
export const clientLoader = async ({ params, request }) => {
	try {
		const url = new URL(request.url);
		const page = url.searchParams.get('page') || 0;
		const per = url.searchParams.get('per') || 10;
		const sort_column = url.searchParams.get('sort_column') || 'created_at';
		const sort_order = url.searchParams.get('sort_order') || 'desc';

		const query = `?page=${page}&per=${per}&sort_column=${sort_column}&sort_order=${sort_order}`;

		const jobsPromise = authedApiFetch({
			endpoint: `/email/jobs${query}`,
			method: 'GET',
		});

		const connectionsPromise = authedApiFetch({
			endpoint: `/tenants/${params.tenantId}/connections`,
			method: 'GET',
		});

		const [jobs, connections] = await Promise.all([
			jobsPromise,
			connectionsPromise,
		]);

		return { jobs, connections };
	} catch (error) {
		console.error('Error loading email jobs:', error);
		throw error;
	}
};

// Creates email sync job
export const action = async ({ request }) => {
	try {
		const formData = await request.formData();
		const provider = formData.get('provider');
		const range_start = formData.get('range_start');
		const range_end = formData.get('range_end');

		if (!provider) {
			throw new Error('Provider is required');
		}

		if (!range_start || !range_end) {
			throw new Error('Range start and end are required');
		}

		const res = await authedApiFetch({
			endpoint: `/email/jobs`,
			method: 'POST',
			payload: {
				email_job: {
					provider,
					range_start,
					range_end,
				},
			},
			returnResponse: true, // to retrieve error from sync datetime range
		});

		const job = await res.json();

		if (res.status === 422) {
			return { success: false, message: job.error };
		} else {
			return job.status === 'canceled'
				? { success: true, message: job.status_detail?.cause }
				: { success: true, message: 'Successfully started email job' };
		}
	} catch (error) {
		console.error('Error creating email job:', error);

		return {
			success: false,
			message: 'Failed to start email job',
		};
	}
};

const MICROSOFT_SERVICE_ID = 23;
const MICROSOFT_SERVICE_ID_LEGACY = 13;
const GOOGLE_SERVICE_ID = 21; // Google service for Email Sync (gmail.readonly scope)
const EMAIL_SYNC_FLAG_KEYS = ['show-trigger-email-auto-sync-button'];

const isMicrosoftServiceId = (serviceId) =>
	serviceId === MICROSOFT_SERVICE_ID ||
	serviceId === MICROSOFT_SERVICE_ID_LEGACY;

const EmailSync = () => {
	// MUI theme hook
	const theme = useTheme();

	// React Router hooks
	const { tenant, user, showNotification } = useOutletContext();
	const revalidator = useRevalidator();
	const navigate = useNavigate();

	// Use custom hook to manage creation of Composio connection
	const { initiateComposioOAuth, oAuthResult } = useComposioOAuth({
		tenantId: tenant?.id,
	});

	// Use jobs from poller if available, else fallback to loader
	const pollerFetcher = useFetcher();

	const {
		parameters: initialQueryParams,
		handlePaginationModelChange,
		handleSortModelChange,
	} = useDataGridState();

	const { jobs: loaderJobs, connections } = useLoaderData();
	const { jobs, jobCount, queryParams } = useEmailJobsPoller({
		loaderJobs,
		initialQueryParams,
	});

	const outlookConnection = useMemo(() => {
		return connections.find(
			(c) => isMicrosoftServiceId(c.service_id) && c.user_id === user?.id
		);
	}, [connections, user?.id]);

	const gmailConnection = useMemo(() => {
		return connections.find(
			(c) => c.service_id === GOOGLE_SERVICE_ID && c.user_id === user?.id
		);
	}, [connections, user?.id]);

	const userHasOutlookConnection = useMemo(() => {
		return outlookConnection?.connection_status === 'connected';
	}, [outlookConnection]);

	const userHasGmailConnection = useMemo(() => {
		return gmailConnection?.connection_status === 'connected';
	}, [gmailConnection]);

	const hasDisruptedConnection = useMemo(() => {
		return (
			isConnectionDisrupted(outlookConnection) ||
			isConnectionDisrupted(gmailConnection)
		);
	}, [outlookConnection, gmailConnection]);

	const userHasAnyEmailConnection = useMemo(() => {
		return connections.some(
			(c) =>
				(isMicrosoftServiceId(c.service_id) ||
					c.service_id === GOOGLE_SERVICE_ID) &&
				c.connection_status !== 'disconnected' &&
				c.user_id === user?.id
		);
	}, [connections, user?.id]);

	const availableProviders = useMemo(() => {
		const providers = [];
		if (userHasOutlookConnection) providers.push('microsoft');
		if (userHasGmailConnection) providers.push('google');
		return providers;
	}, [userHasOutlookConnection, userHasGmailConnection]);

	// Fetchers for form submissions
	const startJobFetcher = useFetcher();
	const syncJobFetcher = useFetcher();
	const cancelJobFetcher = useFetcher();

	// State management
	const [activeModal, setActiveModal] = useState(null); //'emailSyncConfig', 'emailJobConfig', 'emailManualSyncConfirmation
	const [jobError, setJobError] = useState(null);
	const [emailSyncConfigs, setEmailSyncConfigs] = useState([]);
	const [jobIdForManualSync, setJobIdForManualSync] = useState(null);

	// Feature flags
	const { flagStatuses } = useFeatureFlags(tenant, EMAIL_SYNC_FLAG_KEYS);
	const showTriggerButton =
		flagStatuses['show-trigger-email-auto-sync-button'] ?? false;

	const autoSyncEnabledProviders = useMemo(() => {
		return emailSyncConfigs
			.filter((config) => config.enabled)
			.map((config) => config.provider);
	}, [emailSyncConfigs]);

	// Get providers available for manual jobs (those without auto-sync enabled)
	const manualJobProviders = useMemo(() => {
		return availableProviders.filter(
			(provider) => !autoSyncEnabledProviders.includes(provider)
		);
	}, [availableProviders, autoSyncEnabledProviders]);

	// Check if all providers have auto-sync enabled
	const allProvidersAutoSync = useMemo(() => {
		return (
			availableProviders.length > 0 &&
			availableProviders.every((provider) =>
				autoSyncEnabledProviders.includes(provider)
			)
		);
	}, [availableProviders, autoSyncEnabledProviders]);

	const sevenDaysAgo = dayjs().subtract(7, 'days').startOf('minutes');
	// Get provider-specific last sync times from API response
	// Backend queries all jobs (not just paginated) to find the most recent synced job per provider
	const lastSyncEndMicrosoft =
		pollerFetcher.data?.jobs?.last_sync_end_microsoft ??
		loaderJobs.last_sync_end_microsoft ??
		null;

	const lastSyncEndGoogle =
		pollerFetcher.data?.jobs?.last_sync_end_google ??
		loaderJobs.last_sync_end_google ??
		null;

	// Setup data for the EmailSyncHealthBar component
	const syncSuccessRate =
		pollerFetcher.data?.jobs?.sync_success_rate ??
		loaderJobs.sync_success_rate ??
		0;

	const lastSuccessfulIngestion =
		pollerFetcher.data?.jobs?.last_successful_ingestion ??
		loaderJobs.last_successful_ingestion ??
		null;

	const failedJobCount =
		pollerFetcher.data?.jobs?.failed_job_count ??
		loaderJobs.failed_job_count ??
		null;

	// total interactions synced, sum of all synced jobs
	const totalInteractionCount =
		pollerFetcher.data?.jobs?.total_interaction_count ??
		loaderJobs.total_interaction_count ??
		null;

	// Calculate provider-specific min dates for auto-sync date pickers
	const microsoftSyncStartMin =
		lastSyncEndMicrosoft && dayjs(lastSyncEndMicrosoft).isAfter(sevenDaysAgo)
			? dayjs(lastSyncEndMicrosoft)
			: sevenDaysAgo;

	const googleSyncStartMin =
		lastSyncEndGoogle && dayjs(lastSyncEndGoogle).isAfter(sevenDaysAgo)
			? dayjs(lastSyncEndGoogle)
			: sevenDaysAgo;

	// Check if the most recent job is blocking new jobs (for button/tooltip)

	const mostRecentStatus =
		Array.isArray(jobs) && jobs.length > 0 ? jobs[0].status : null;
	const isMostRecentJobBlocking = [
		'starting',
		'ingesting',
		'ingested',
		'syncing',
	].includes(mostRecentStatus);

	const startJobDisabledTooltip = useMemo(() => {
		if (hasDisruptedConnection) {
			return 'One of your connections has been interrupted and requires re-authentication before ingestion jobs can resume.';
		}
		if (allProvidersAutoSync) {
			return 'All providers have automatic sync enabled';
		}
		if (isMostRecentJobBlocking) {
			return 'A job is currently in progress or awaiting sync.';
		}
		return null;
	}, [hasDisruptedConnection, allProvidersAutoSync, isMostRecentJobBlocking]);

	// Fetch email sync configurations
	const fetchEmailSyncConfigs = useCallback(async () => {
		if (!tenant?.id || !user?.id) return;
		try {
			const configs = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/users/${user.id}/email_sync_configurations`,
				method: 'GET',
			});
			setEmailSyncConfigs(Array.isArray(configs) ? configs : []);
		} catch (error) {
			console.error('Failed to fetch email sync configurations:', error);
			setEmailSyncConfigs([]);
		}
	}, [tenant?.id, user?.id]);

	useEffect(() => {
		fetchEmailSyncConfigs();
	}, [fetchEmailSyncConfigs]);

	const handleStartIngestion = async (provider, range_start, range_end) => {
		startJobFetcher.submit(
			{ provider, range_start, range_end },
			{ method: 'post' }
		);
	};

	const handleOpenManualSyncConfimationModal = (jobId) => {
		setJobIdForManualSync(jobId);
		setActiveModal('emailManualSyncConfirmation');
	};

	const handleSync = () => {
		syncJobFetcher.submit(
			{ jobIdForManualSync },
			{ method: 'post', action: `${jobIdForManualSync}/sync` }
		);
		handleCloseModal();
	};

	const handleCancel = (jobId) => {
		cancelJobFetcher.submit(
			{ jobId },
			{ method: 'post', action: `${jobId}/cancel` }
		);
	};

	const handleTriggerAutoSync = async () => {
		try {
			await authedApiFetch({
				endpoint: '/email/jobs/trigger_auto_sync',
				method: 'POST',
			});
			showNotification('Email auto sync job triggered successfully', 'success');
			revalidator.revalidate();
		} catch (error) {
			console.error('Failed to trigger auto sync:', error);
			showNotification('Failed to trigger email auto sync job', 'error');
		}
	};

	const handleCloseModal = () => {
		setActiveModal(null);
		setJobError(null);
		setJobIdForManualSync(null);
	};

	// Handle action responses
	useEffect(() => {
		const fetcherData =
			startJobFetcher.data || syncJobFetcher.data || cancelJobFetcher.data;

		if (fetcherData) {
			// if create job is successful, close the EmailJobConfigModal, else keep it open to display error
			if (activeModal === 'emailJobConfig') {
				if (fetcherData.success) {
					handleCloseModal();
				} else {
					// display sync range error within the modal while it's still open
					setJobError(fetcherData.message);
				}
			}

			// if EmailJobConfigModal is not open, display notification as usual
			showNotification(
				fetcherData.message,
				fetcherData.success ? 'success' : 'error'
			);
		}
		// if it is listed here as a dependency. Then remove linter disabler line.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startJobFetcher.data, syncJobFetcher.data, cancelJobFetcher.data]);

	const [msAuthLoading, setMsAuthLoading] = useState(false);
	const [gmailAuthLoading, setGmailAuthLoading] = useState(false);

	const handleMsLogin = () => {
		setMsAuthLoading(true);
		initiateComposioOAuth(MICROSOFT_SERVICE_ID);
	};

	const handleGmailLogin = () => {
		setGmailAuthLoading(true);
		initiateComposioOAuth(GOOGLE_SERVICE_ID);
	};

	const outlookButtonState = useMemo(() => {
		return getConnectionButtonState(
			outlookConnection,
			'Outlook',
			msAuthLoading
		);
	}, [outlookConnection, msAuthLoading]);

	const gmailButtonState = useMemo(() => {
		return getConnectionButtonState(gmailConnection, 'Gmail', gmailAuthLoading);
	}, [gmailConnection, gmailAuthLoading]);

	// React to OAuth result changes (success or error)
	useEffect(() => {
		if (oAuthResult) {
			const serviceType = isMicrosoftServiceId(oAuthResult.service_id)
				? 'Outlook'
				: 'Gmail';

			if (oAuthResult.success === false) {
				showNotification(
					oAuthResult.error || `Failed to connect ${serviceType}`,
					'error'
				);
			} else if (oAuthResult.success === true) {
				showNotification(`Successfully connected ${serviceType}`, 'success');

				// Re-fetch data from loader (to get new connection)
				revalidator.revalidate();
			}

			// Reset loading state for the appropriate service
			if (isMicrosoftServiceId(oAuthResult.service_id)) {
				setMsAuthLoading(false);
			} else if (oAuthResult.service_id === GOOGLE_SERVICE_ID) {
				setGmailAuthLoading(false);
			}
		}
		// if it is listed here as a dependency. Then remove linter disabler line.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [oAuthResult]);

	const columns = [
		{
			field: 'provider',
			headerName: 'Provider',
			cellClassName: 'capitalize-text',
			flex: 2,
		},
		{
			field: 'trigger_type',
			headerName: 'Type',
			flex: 2,
			valueGetter: (value) => (value === 'manual' ? 'Manual' : 'Automatic'),
		},
		{
			field: 'range_start',
			headerName: 'Range Start',
			flex: 2,
			valueGetter: (value) => formatDateTime(value),
		},
		{
			field: 'range_end',
			headerName: 'Range End',
			flex: 2,
			valueGetter: (value) => formatDateTime(value),
		},
		{
			field: 'created_at',
			headerName: 'Created At',
			flex: 2,
			valueGetter: (value) => formatDateTime(value),
		},
		{
			field: 'status',
			headerName: 'Status',
			cellClassName: 'capitalize-text',
			flex: 2,
			renderCell: (params) => (
				<Box
					sx={{
						display: 'flex',
						columnGap: 1,
						alignItems: 'center',
					}}
				>
					<span>{params.value}</span>
					{(params.value === 'starting' ||
						params.value === 'ingesting' ||
						params.value === 'syncing') && <CircularProgress size={16} />}
				</Box>
			),
		},
		{
			field: 'status_detail',
			headerName: 'Status Detail',
			flex: 2,
			renderCell: (params) => {
				if (
					params?.row?.status === 'ingested' &&
					params?.row?.statistics &&
					Object.prototype.hasOwnProperty.call(
						params?.row?.statistics,
						'emails_ingested'
					)
				) {
					const emailCount =
						params.row.statistics.emails_ingested.toLocaleString();
					const failedEmailCount =
						params.row.statistics.failed_emails_ingested?.toLocaleString() || 0;
					return (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								width: '100%',
							}}
						>
							<span>{emailCount} emails pulled</span>
							{failedEmailCount > 0 && (
								<FailedIngestionCount count={failedEmailCount} />
							)}
						</Box>
					);
				}

				if (
					params?.row?.status === 'synced' &&
					params?.row?.statistics &&
					Object.prototype.hasOwnProperty.call(
						params?.row?.statistics,
						'interaction_messages_synced'
					)
				) {
					const emailCount =
						params.row.statistics.interaction_messages_synced.toLocaleString();
					return <span>{emailCount} emails synced to CRM</span>;
				}

				if (params?.row?.status_detail) {
					return <span>{JSON.stringify(params?.row?.status_detail)}</span>;
				}

				return null;
			},
		},
		{
			field: 'actions',
			headerName: 'Actions',
			type: 'actions',
			flex: 1,
			getActions: (params) => [
				<GridActionsCellItem
					key={`view-${params.row.id}`}
					icon={<VisibilityIcon />}
					onClick={() => navigate(`${params.row.id}/records`)}
					label='View Records'
				/>,
				<GridActionsCellItem
					key={`sync-${params.row.id}`}
					icon={<SyncIcon />}
					onClick={() => handleOpenManualSyncConfimationModal(params.row.id)}
					label='Sync'
					showInMenu
					disabled={
						params.row.status !== 'ingested' ||
						syncJobFetcher.state === 'submitting'
					}
				/>,
				<GridActionsCellItem
					key={`cancel-${params.row.id}`}
					icon={<CancelIcon />}
					onClick={() => handleCancel(params.row.id)}
					label='Cancel'
					showInMenu
					disabled={
						!['starting', 'ingested'].includes(params.row.status) ||
						cancelJobFetcher.state === 'submitting'
					}
				/>,
			],
		},
	];

	return (
		<Box
			sx={{
				py: 0,
				pt: 1.5,
				width: '100%',
				height: 'calc(100vh - 64px)',
				overflow: 'auto',
			}}
		>
			<EmailSyncTitle />
			<Typography variant='body1' sx={{ mb: 3 }}>
				Connect your email systems (Gmail or Microsoft Exchange) to
				automatically extract and sync valuable business relationships. Email
				Sync identifies accounts, contacts, and all related interactions from
				your email data, providing deep relationship intelligence within Altvia.
				Stay connected to your network with automated email synchronization that
				keeps your CRM data current and comprehensive.
			</Typography>

			<Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
				<Paper
					elevation={0}
					sx={{
						p: 3,
						backgroundColor: theme.palette.background.paper,
						border: `1px solid ${theme.palette.divider}`,
					}}
				>
					<Typography variant='h6' gutterBottom>
						Email Connection
					</Typography>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
						{outlookButtonState.tooltip ? (
							<Tooltip title={outlookButtonState.tooltip}>
								<span>
									<LoadingButton
										variant='contained'
										color={outlookButtonState.color}
										startIcon={<MicrosoftIcon />}
										onClick={handleMsLogin}
										disabled={outlookButtonState.disabled}
										loading={outlookButtonState.loading}
										loadingPosition='start'
										sx={{
											mr: 3,
										}}
									>
										{outlookButtonState.label}
									</LoadingButton>
								</span>
							</Tooltip>
						) : (
							<LoadingButton
								variant='contained'
								color={outlookButtonState.color}
								startIcon={<MicrosoftIcon />}
								onClick={handleMsLogin}
								disabled={outlookButtonState.disabled}
								loading={outlookButtonState.loading}
								loadingPosition='start'
								sx={{
									mr: 3,
								}}
							>
								{outlookButtonState.label}
							</LoadingButton>
						)}
						{gmailButtonState.tooltip ? (
							<Tooltip title={gmailButtonState.tooltip}>
								<span>
									<LoadingButton
										variant='contained'
										color={gmailButtonState.color}
										startIcon={<GoogleIcon />}
										onClick={handleGmailLogin}
										disabled={gmailButtonState.disabled}
										loading={gmailButtonState.loading}
										loadingPosition='start'
										sx={{ mr: 2 }}
									>
										{gmailButtonState.label}
									</LoadingButton>
								</span>
							</Tooltip>
						) : (
							<LoadingButton
								variant='contained'
								color={gmailButtonState.color}
								startIcon={<GoogleIcon />}
								onClick={handleGmailLogin}
								disabled={gmailButtonState.disabled}
								loading={gmailButtonState.loading}
								loadingPosition='start'
								sx={{ mr: 2 }}
							>
								{gmailButtonState.label}
							</LoadingButton>
						)}
					</Box>
					<Typography variant='body2' color='text.secondary'>
						Connect your email accounts to automatically extract business
						relationships and interactions.
					</Typography>
				</Paper>

				{userHasAnyEmailConnection ? (
					<>
						<EmailSyncHealthBar
							jobCount={jobCount}
							userHasOutlookConnection={userHasOutlookConnection}
							userHasGmailConnection={userHasGmailConnection}
							syncSuccessRate={syncSuccessRate}
							lastSuccessfulIngestion={lastSuccessfulIngestion}
							failedJobCount={failedJobCount}
							totalInteractionCount={totalInteractionCount}
						/>

						<Button
							sx={{ width: '100%', height: 80 }}
							variant='outlined'
							startIcon={<SettingsSuggestOutlinedIcon />}
							onClick={() => setActiveModal('emailSyncConfig')}
						>
							Configure Sync
						</Button>
						<Box
							sx={{
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									mb: 2,
								}}
							>
								<Typography variant='h6'>Email Ingestion Jobs</Typography>
								<Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
									{showTriggerButton && (
										<LoadingButton
											variant='outlined'
											onClick={handleTriggerAutoSync}
											disabled={isMostRecentJobBlocking}
										>
											Trigger Auto Sync (QA)
										</LoadingButton>
									)}
									{startJobDisabledTooltip ? (
										<Tooltip title={startJobDisabledTooltip}>
											<span>
												<LoadingButton
													variant='outlined'
													startIcon={<ForwardToInboxIcon />}
													disabled
												>
													Start New Job
												</LoadingButton>
											</span>
										</Tooltip>
									) : (
										<LoadingButton
											variant='outlined'
											startIcon={<ForwardToInboxIcon />}
											onClick={() => setActiveModal('emailJobConfig')}
											loading={
												startJobFetcher.state === 'submitting' &&
												activeModal !== 'emailJobConfig'
											}
											disabled={startJobFetcher.state === 'submitting'}
										>
											Start New Job
										</LoadingButton>
									)}
								</Box>
							</Box>

							<DataGrid
								sx={styles.capitalizeText}
								rows={jobs}
								columns={columns}
								paginationMode='server'
								paginationModel={{
									page: Number(queryParams.page),
									pageSize: Number(queryParams.per),
								}}
								onPaginationModelChange={handlePaginationModelChange}
								rowCount={jobCount}
								sortingMode='server'
								sortModel={
									queryParams.sort_column
										? [
												{
													field: queryParams.sort_column,
													sort: queryParams.sort_order,
												},
											]
										: []
								}
								onSortModelChange={handleSortModelChange}
								pageSizeOptions={[5, 10, 25]}
								disableRowSelectionOnClick
							/>
						</Box>
					</>
				) : (
					<Box
						sx={{
							height: 400,
							width: '100%',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<Typography>
							Connect your email account to begin syncing data.
						</Typography>
					</Box>
				)}

				<UserEmailSyncConfigModal
					tenant={tenant}
					user={user}
					microsoftSyncStartMin={microsoftSyncStartMin}
					googleSyncStartMin={googleSyncStartMin}
					isMostRecentJobBlocking={isMostRecentJobBlocking}
					open={activeModal === 'emailSyncConfig'}
					onClose={handleCloseModal}
					showNotification={showNotification}
					availableProviders={availableProviders}
					onConfigChange={fetchEmailSyncConfigs}
				/>

				<EmailJobConfigModal
					lastSyncEndMicrosoft={microsoftSyncStartMin}
					lastSyncEndGoogle={googleSyncStartMin}
					open={activeModal === 'emailJobConfig'}
					onClose={handleCloseModal}
					onConfirm={handleStartIngestion}
					loading={startJobFetcher.state === 'submitting'}
					error={jobError}
					setError={setJobError}
					availableProviders={manualJobProviders}
				/>

				<EmailManualSyncConfirmationModal
					open={activeModal === 'emailManualSyncConfirmation'}
					onClose={handleCloseModal}
					onConfirm={handleSync}
				/>
			</Box>
		</Box>
	);
};

export default EmailSync;
